import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu, ChevronRight } from 'lucide-react';
import { BRAND_LOGO } from '@/lib/logos';
import { InstallAppButton } from '@/components/InstallAppButton';

const NAV_LINKS = [
    { to: '/', label: 'Home' },
    { to: '/platforms/olaparty', label: 'Olaparty' },
    { to: '/platforms/trumeet', label: 'Trumeet' },
    { to: '/policies/refund', label: 'Policies' },
];

export function PublicHeader() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    return (
        <header className="sticky top-0 z-40 border-b border-border bg-white/95 backdrop-blur-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2" data-testid="public-nav-logo">
                    <img src={BRAND_LOGO} alt="Eagle Eyes" className="w-9 h-9 rounded-lg object-cover ring-1 ring-border" />
                    <div className="leading-tight">
                        <div className="font-display font-semibold text-[15px] text-foreground">Eagle Eyes</div>
                        <div className="text-[11px] text-muted-foreground -mt-0.5">Top-up Service</div>
                    </div>
                </Link>
                <nav className="hidden md:flex items-center gap-1">
                    {NAV_LINKS.map((l) => (
                        <Link
                            key={l.to}
                            to={l.to}
                            data-testid={`public-nav-${l.label.toLowerCase()}`}
                            className={`px-3 py-2 rounded-md text-sm font-medium hover:bg-secondary transition-colors ${location.pathname === l.to ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                        >{l.label}</Link>
                    ))}
                </nav>
                <div className="hidden md:flex items-center gap-2">
                    <InstallAppButton testid="public-install-app-button" className="border-[#1F9D8A] text-[#0E2A47] hover:bg-[#1F9D8A]/10" />
                    {user ? (
                        <>
                            <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="public-nav-dashboard-button">Dashboard</Button>
                            <Button variant="outline" onClick={logout} data-testid="public-nav-logout-button">Logout</Button>
                        </>
                    ) : (
                        <>
                            <Button variant="ghost" onClick={() => navigate('/login')} data-testid="public-nav-login-button">Login</Button>
                            <Button onClick={() => navigate('/recharge')} data-testid="public-nav-recharge-button" className="bg-[#0E2A47] hover:bg-[#0B223A]">Recharge Now</Button>
                        </>
                    )}
                </div>
                <Sheet>
                    <SheetTrigger asChild>
                        <Button variant="ghost" size="icon" className="md:hidden" data-testid="public-nav-menu-button"><Menu className="h-5 w-5" /></Button>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-72">
                        <div className="py-6 space-y-1">
                            {NAV_LINKS.map((l) => (
                                <Link key={l.to} to={l.to} className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-secondary">
                                    <span className="text-sm font-medium">{l.label}</span>
                                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                </Link>
                            ))}
                            <div className="pt-4 border-t border-border space-y-2">
                                <InstallAppButton testid="public-install-app-button-mobile" className="w-full justify-center border-[#1F9D8A] text-[#0E2A47]" />
                                {user ? (
                                    <>
                                        <Button className="w-full" onClick={() => navigate('/dashboard')}>Dashboard</Button>
                                        <Button variant="outline" className="w-full" onClick={logout}>Logout</Button>
                                    </>
                                ) : (
                                    <>
                                        <Button className="w-full bg-[#0E2A47]" onClick={() => navigate('/login')}>Login</Button>
                                        <Button variant="outline" className="w-full" onClick={() => navigate('/recharge')}>Recharge Now</Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </SheetContent>
                </Sheet>
            </div>
        </header>
    );
}

export function PublicFooter() {
    return (
        <footer className="bg-[#0E2A47] text-slate-300 mt-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                <div>
                    <div className="flex items-center gap-2">
                        <img src={BRAND_LOGO} alt="Eagle Eyes" className="w-8 h-8 rounded-md object-cover" />
                        <div className="text-white font-display font-semibold">Eagle Eyes Services</div>
                    </div>
                    <p className="mt-3 text-sm text-slate-400">A reliable diamond &amp; coin top-up service for Olaparty and Trumeet.</p>
                </div>
                <div>
                    <div className="text-white font-medium mb-3">Platforms</div>
                    <ul className="space-y-2 text-sm">
                        <li><Link className="hover:text-white" to="/platforms/olaparty">Olaparty</Link></li>
                        <li><Link className="hover:text-white" to="/platforms/trumeet">Trumeet</Link></li>
                    </ul>
                </div>
                <div>
                    <div className="text-white font-medium mb-3">Legal</div>
                    <ul className="space-y-2 text-sm">
                        <li><Link className="hover:text-white" to="/policies/refund">Refund Policy</Link></li>
                        <li><Link className="hover:text-white" to="/policies/privacy">Privacy Policy</Link></li>
                        <li><Link className="hover:text-white" to="/policies/fair-use">Fair Use Policy</Link></li>
                        <li><Link className="hover:text-white" to="/policies/terms">Terms of Service</Link></li>
                    </ul>
                </div>
                <div>
                    <div className="text-white font-medium mb-3">Team Access</div>
                    <ul className="space-y-2 text-sm">
                        <li><Link className="hover:text-white" to="/reseller/login">Reseller Login</Link></li>
                    </ul>
                </div>
            </div>
            <div className="border-t border-white/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="text-xs text-slate-400" data-testid="public-footer-company">
                        © {new Date().getFullYear()} <span className="font-semibold text-white">Shinning Eyes Entertainment Private Limited</span>. All rights reserved. Eagle Eyes Services is a brand of Shinning Eyes Entertainment Pvt Ltd.
                    </div>
                    <div className="text-xs text-slate-400">Digital goods are non-reversible. Entertainment use only - not for gambling.</div>
                </div>
            </div>
        </footer>
    );
}

export function PublicLayout({ children }) {
    return (
        <div className="min-h-screen flex flex-col bg-background">
            <PublicHeader />
            <main className="flex-1">{children}</main>
            <PublicFooter />
        </div>
    );
}
